import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MapPin, Shield, ArrowLeft, MessageCircle, Loader2, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { loginWithGoogle, otpSend, otpVerify, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('choose'); // choose | otp-phone | otp-code | email
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('+52');
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phone.length < 10) return toast.error('Ingresa un número válido con lada, ej: +525512345678');
    setLoading(true);
    try {
      const res = await otpSend(phone);
      if (res.sent === false) {
        toast.info('Twilio está en Sandbox — el código está en los logs del servidor');
      } else {
        toast.success('Código enviado a tu WhatsApp');
      }
      setMode('otp-code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error enviando el código');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error('Ingresa los 6 dígitos');
    setLoading(true);
    try {
      await otpVerify(phone, code);
      toast.success('Bienvenido');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código incorrecto');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Ingresa correo y contraseña');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(res.data.user);
      toast.success('Bienvenido');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FAFAFA] to-[#F1F5F9] p-4 safe-top safe-bottom safe-x" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <img src="/logo.png" alt="Ciudad Inteligente" className="w-10 h-10" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="w-10 h-10 rounded-md bg-[#0052FF] flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Ciudad Inteligente
          </h1>
          <p className="text-sm text-[#52525B] mt-1">Plataforma de gestión urbana ciudadana</p>
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-xl shadow-sm p-6 space-y-4">
          {mode === 'choose' && (
            <>
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold text-[#0A0A0A]">Iniciar sesión</h2>
                <p className="text-xs text-[#52525B] mt-0.5">Elige cómo quieres acceder</p>
              </div>

              <Button
                data-testid="google-login-btn"
                onClick={loginWithGoogle}
                variant="outline"
                className="w-full h-12 rounded-md font-semibold border-[#E4E4E7] hover:bg-[#F4F4F5] hover:border-[#0A0A0A] text-[#0A0A0A]"
              >
                <GoogleIcon />
                Continuar con Google
              </Button>

              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E4E4E7]" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-[10px] uppercase tracking-widest text-[#52525B] font-bold">o</span></div>
              </div>

              <Button
                data-testid="whatsapp-login-btn"
                onClick={() => setMode('otp-phone')}
                variant="outline"
                className="w-full h-12 rounded-md font-semibold border-[#E4E4E7] hover:bg-[#25D366] hover:border-[#25D366] hover:text-white text-[#0A0A0A]"
              >
                <MessageCircle className="w-5 h-5 mr-2 text-[#25D366]" />
                Continuar con WhatsApp
              </Button>

              <Button
                data-testid="email-login-btn"
                onClick={() => setMode('email')}
                variant="outline"
                className="w-full h-12 rounded-md font-semibold border-[#E4E4E7] hover:bg-[#0052FF] hover:border-[#0052FF] hover:text-white text-[#0A0A0A]"
              >
                <Mail className="w-5 h-5 mr-2 text-[#0052FF]" />
                Continuar con correo y contraseña
              </Button>

              <div className="pt-3 border-t border-[#F4F4F5]">
                <p className="text-[11px] text-[#52525B] flex items-center gap-1.5 justify-center">
                  <Shield className="w-3 h-3" />
                  Acceso restringido a usuarios autorizados
                </p>
              </div>
            </>
          )}

          {mode === 'email' && (
            <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-form">
              <button type="button" onClick={() => setMode('choose')} className="text-xs text-[#52525B] hover:text-[#0A0A0A] flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Regresar
              </button>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#0052FF]/10 flex items-center justify-center mx-auto mb-2">
                  <Mail className="w-7 h-7 text-[#0052FF]" />
                </div>
                <h2 className="text-lg font-bold text-[#0A0A0A]">Correo y contraseña</h2>
                <p className="text-xs text-[#52525B]">Usa el correo y contraseña que te asignó tu jefe</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-bold text-[#52525B]">Correo</Label>
                <Input
                  data-testid="email-input"
                  type="email"
                  className="h-12 rounded-md"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu-correo@ejemplo.com"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-bold text-[#52525B]">Contraseña</Label>
                <Input
                  data-testid="password-input"
                  type="password"
                  className="h-12 rounded-md"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <Button data-testid="email-submit-btn" type="submit" disabled={loading} className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-bold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Iniciar sesión'}
              </Button>
            </form>
          )}

          {mode === 'otp-phone' && (
            <form onSubmit={handleSendOTP} className="space-y-4" data-testid="otp-phone-form">
              <button type="button" onClick={() => setMode('choose')} className="text-xs text-[#52525B] hover:text-[#0A0A0A] flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Regresar
              </button>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#25D366]/10 flex items-center justify-center mx-auto mb-2">
                  <MessageCircle className="w-7 h-7 text-[#25D366]" />
                </div>
                <h2 className="text-lg font-bold text-[#0A0A0A]">Login con WhatsApp</h2>
                <p className="text-xs text-[#52525B]">Te enviaremos un código de 6 dígitos</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-bold text-[#52525B]">Número WhatsApp</Label>
                <Input
                  data-testid="phone-input"
                  type="tel"
                  className="h-12 rounded-md font-mono text-base"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+52 55 1234 5678"
                  autoFocus
                />
                <p className="text-[10px] text-[#52525B]">Incluye lada del país (+52 para México)</p>
              </div>
              <Button data-testid="send-otp-btn" type="submit" disabled={loading} className="w-full h-12 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-md font-bold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar código'}
              </Button>
            </form>
          )}

          {mode === 'otp-code' && (
            <form onSubmit={handleVerifyOTP} className="space-y-4" data-testid="otp-code-form">
              <button type="button" onClick={() => setMode('otp-phone')} className="text-xs text-[#52525B] hover:text-[#0A0A0A] flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Cambiar número
              </button>
              <div className="text-center">
                <h2 className="text-lg font-bold text-[#0A0A0A]">Ingresa tu código</h2>
                <p className="text-xs text-[#52525B]">Enviado a <span className="font-mono font-bold">{phone}</span></p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-bold text-[#52525B]">Código de 6 dígitos</Label>
                <Input
                  data-testid="code-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="h-14 rounded-md text-center font-mono text-2xl tracking-[0.6em] font-black"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  autoFocus
                />
              </div>
              <Button data-testid="verify-otp-btn" type="submit" disabled={loading || code.length !== 6} className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-bold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Iniciar sesión'}
              </Button>
              <button
                type="button"
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full text-xs text-[#0052FF] hover:underline font-medium"
              >
                Reenviar código
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-[#A1A1AA] mt-6">
          © 2026 Ciudad Inteligente · CDMX
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
